import React from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RotateCcw, Home } from "lucide-react";

/** App-wide error boundary.
 *
 * Without this, any uncaught render error (e.g. trying to render a FastAPI 422
 * validation object as a React child) unmounts the entire React tree and the
 * user sees a blank white screen with no way to recover. With this in place,
 * they see a friendly fallback with "Reload" and "Go Home" buttons.
 *
 * Class component is required — error boundaries can only be classes (React
 * has no functional-component equivalent yet as of React 19).
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surface to the browser console so users can copy/paste it into a bug report.
    // eslint-disable-next-line no-console
    console.error("App crash caught by ErrorBoundary:", error, info);
  }

  handleReload = () => {
    // Hard reload bypasses any corrupt state in memory.
    if (typeof window !== "undefined") window.location.reload();
  };

  handleHome = () => {
    if (typeof window !== "undefined") window.location.href = "/";
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const message = this.state.error?.message || "An unexpected error occurred.";
    return (
      <div
        className="min-h-screen flex items-center justify-center p-6 bg-slate-50 dark:bg-slate-950"
        data-testid="app-error-boundary"
      >
        <div className="max-w-md w-full bg-white dark:bg-slate-900 rounded-xl shadow-lg border border-rose-200 dark:border-rose-800 p-8 text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-rose-100 dark:bg-rose-900/40 grid place-items-center mb-4">
            <AlertTriangle className="w-7 h-7 text-rose-600 dark:text-rose-400" />
          </div>
          <h1 className="font-display text-xl font-bold text-slate-900 dark:text-slate-100">
            Something went wrong
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">
            We hit an unexpected error and couldn&apos;t show this page. Reloading usually fixes it.
          </p>
          <div className="mt-4 p-3 rounded-md bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-left">
            <code className="text-[11px] text-slate-700 dark:text-slate-300 break-all">{message}</code>
          </div>
          <div className="mt-6 flex gap-2 justify-center">
            <Button
              onClick={this.handleReload}
              className="bg-[#1E3A8A] hover:bg-[#1E40AF]"
              data-testid="error-boundary-reload-btn"
            >
              <RotateCcw className="w-4 h-4 mr-1.5" /> Reload page
            </Button>
            <Button
              variant="outline"
              onClick={this.handleHome}
              data-testid="error-boundary-home-btn"
            >
              <Home className="w-4 h-4 mr-1.5" /> Go home
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
